import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "./AuthContext";
import { fetchCreditBalance, updateCreditBalance } from "@/services/db/credits";
import { isInsufficientCreditsError } from "@/lib/credits/isInsufficientCreditsError";
import { INITIAL_CREDITS, INITIAL_MAX_CREDITS } from "@/config/plans";
import { REFERRAL_BONUS_GRANTED_EVENT } from "@/config/referral";
import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";

/** Why the pricing modal opened — drives the `surface` property on the
 *  `credits_insufficient_shown` analytics event so we can see which trigger
 *  surfaces the upsell most. */
type PricingTriggerSurface =
  | "local_balance_check"
  | "api_error"
  | "credit_wall"
  | "starter_plan_required";

/** Optional context that drives which copy the PricingModal shows. Default
 *  (null) → the credit-wall copy. "starter_plan_required" → the feature-gate
 *  copy ("This feature is available on Starter and above…"). Extend as new
 *  upsell triggers appear; keeps the modal a single render path. */
export type PricingModalContext = null | "starter_plan_required";

interface CreditsContextType {
  credits: number;
  maxCredits: number;
  deductCredits: (amount: number) => boolean;
  addCredits: (amount: number, newMax?: number) => Promise<void>;
  hasEnoughCredits: (amount: number) => boolean;
  showPricingModal: boolean;
  setShowPricingModal: (show: boolean) => void;
  pricingModalContext: PricingModalContext;
  /** Opens the pricing modal with a specific copy variant (e.g. plan-gate
   *  vs credit-wall). Pass null/omit for the default credit-wall copy. */
  openPricingModal: (context?: PricingModalContext) => void;
  refreshCredits: () => Promise<void>;
  handleCreditError: (errorOrResponse: unknown) => boolean;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [credits, setCredits] = useState<number>(INITIAL_CREDITS);
  const [showPricingModal, setShowPricingModalState] = useState(false);
  const [pricingModalContext, setPricingModalContext] = useState<PricingModalContext>(null);
  // Tracks the surface that triggered the most recent open so the analytics
  // event fired on render can report a meaningful `surface`.
  const lastTriggerSurfaceRef = useRef<PricingTriggerSurface>("credit_wall");

  const setShowPricingModalWithSurface = useCallback(
    (show: boolean, surface: PricingTriggerSurface) => {
      if (show) lastTriggerSurfaceRef.current = surface;
      setShowPricingModalState(show);
      if (!show) setPricingModalContext(null);
    },
    [],
  );

  /** Closing the modal also clears the context so the next open defaults to
   *  the credit-wall copy unless a new caller opts in. */
  const setShowPricingModal = useCallback((show: boolean) => {
    setShowPricingModalWithSurface(show, "credit_wall");
  }, [setShowPricingModalWithSurface]);

  const openPricingModal = useCallback((ctx: PricingModalContext = null) => {
    setPricingModalContext(ctx);
    setShowPricingModalWithSurface(true, ctx === "starter_plan_required" ? "starter_plan_required" : "credit_wall");
  }, [setShowPricingModalWithSurface]);

  // Fire `credits_insufficient_shown` on every false→true transition. Closes
  // the Credit Wall → Purchase funnel gap: server-side `credits_insufficient`
  // alone can't tell whether the upsell ever surfaced to the user.
  useEffect(() => {
    if (!showPricingModal) return;
    captureEvent(ANALYTICS_EVENTS.CREDITS_INSUFFICIENT_SHOWN, {
      surface: lastTriggerSurfaceRef.current,
      credits_balance: credits,
    });
    // We only want to fire on each transition into open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPricingModal]);

  // Derive maxCredits from profile — single source of truth
  const maxCredits = profile?.credit_limit ?? INITIAL_MAX_CREDITS;

  // Fetch credits from database
  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(INITIAL_CREDITS);
      return;
    }

    try {
      const balance = await fetchCreditBalance(user.id);
      if (balance !== null) {
        setCredits(balance);
      }
    } catch (error) {
      // Outside react-query, so the global MutationCache/QueryCache won't see
      // this. Capture explicitly so a Supabase outage on credits fetch is
      // alertable instead of silently rendering a stale balance.
      Sentry.captureException(error, {
        tags: { type: "credits_fetch_failure" },
      });
    }
  }, [user]);

  // Fetch credits when user changes
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Re-fetch when a referral bonus lands right after signup.
  useEffect(() => {
    const handler = () => {
      void fetchCredits();
    };
    window.addEventListener(REFERRAL_BONUS_GRANTED_EVENT, handler);
    return () => window.removeEventListener(REFERRAL_BONUS_GRANTED_EVENT, handler);
  }, [fetchCredits]);

  const hasEnoughCredits = useCallback((amount: number): boolean => {
    return credits >= amount;
  }, [credits]);

  const deductCredits = useCallback((amount: number): boolean => {
    if (credits < amount) {
      setShowPricingModalWithSurface(true, "local_balance_check");
      return false;
    }
    // Optimistic local update only — backend handles actual DB deduction
    setCredits(prev => prev - amount);
    return true;
  }, [credits, setShowPricingModalWithSurface]);

  const handleCreditError = useCallback((errorOrResponse: unknown): boolean => {
    if (isInsufficientCreditsError(errorOrResponse)) {
      setShowPricingModalWithSurface(true, "api_error");
      return true;
    }
    return false;
  }, [setShowPricingModalWithSurface]);

  const addCredits = useCallback(async (amount: number, newMax?: number) => {
    if (!user) {
      if (newMax !== undefined) {
        setCredits(newMax);
      } else {
        setCredits(prev => prev + amount);
      }
      return;
    }

    const newBalance = newMax !== undefined ? newMax : credits + amount;

    try {
      await updateCreditBalance(user.id, newBalance);
    } catch (error) {
      // Direct supabase call — bypasses the queryClient capture. Real
      // failures here mean the DB balance and the UI are now out of sync,
      // which silently breaks gating logic; alert ops via Sentry.
      Sentry.captureException(error, {
        tags: { type: "credits_update_failure" },
      });
      return;
    }

    setCredits(newBalance);
  }, [user, credits]);

  const refreshCredits = useCallback(async () => {
    await fetchCredits();
  }, [fetchCredits]);

  const value = useMemo(() => ({
    credits,
    maxCredits,
    deductCredits,
    addCredits,
    hasEnoughCredits,
    showPricingModal,
    setShowPricingModal,
    pricingModalContext,
    openPricingModal,
    refreshCredits,
    handleCreditError,
  }), [credits, maxCredits, deductCredits, addCredits, hasEnoughCredits, showPricingModal, setShowPricingModal, pricingModalContext, openPricingModal, refreshCredits, handleCreditError]);

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
}
